import classNames from 'clsx';
import type flatpickr from 'flatpickr';
import React, { useMemo } from 'react';
import type { FieldRenderProps } from 'react-final-form';
import { Field } from 'react-final-form';
import Flatpickr from 'react-flatpickr';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import 'flatpickr/dist/l10n';

import Translation from '@swf/components/Translation/Translation';
import CalIcon from '@swf/core/assets/icons/calendar.svg';
import type { InjectedValidatorProps } from '@swf/core/hoc/withValidator';
import withValidator from '@swf/core/hoc/withValidator';
import usePauseValidator from '@swf/core/hooks/usePauseValidator';
import { select } from '@swf/core/store';
import { dateFormat, getIntlDateFormat, pickerFormat } from '@swf/core/utils/formatting';

import styles from './StarsDatePicker.scss';
import Label from './components/Label/Label';

type StarsDatePickerProps = flatpickr.Options.Options & {
  id: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  icon?: React.ReactElement;
  hideLabel?: boolean;
  showIcon?: boolean;
  hideDateFormat?: boolean;
};

type CalendarControlsProps = StarsDatePickerProps &
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FieldRenderProps<any, HTMLInputElement>;

type OwnProps = StarsDatePickerProps & InjectedValidatorProps;
type Props = OwnProps;

const CalendarControls: React.FC<CalendarControlsProps> = (props) => {
  const {
    id,
    label,
    hideLabel,
    disabled,
    className,
    meta: { invalid, valid, dirtySinceLastSubmit, submitError, error },
    input,
    icon,
    showIcon = true,
    hideDateFormat = false
  } = props;
  const onChange: flatpickr.Options.Hook = (_, selectedDate) => {
    input.onChange(selectedDate);
  };

  const { formatDateToParts } = useIntl();
  const dateParts = formatDateToParts(new Date());

  const datePickerFormat = useMemo(
    () => getIntlDateFormat(dateParts, pickerFormat, true),
    [dateParts]
  );

  const textFormat = useMemo(() => getIntlDateFormat(dateParts, dateFormat), [dateParts]);

  const options: flatpickr.Options.Options = {
    altInput: true,
    altFormat: datePickerFormat,
    altInputClass: styles.input,
    disableMobile: true,
    defaultDate: input.value,
    static: true,
    onChange,
    ...props
  };

  const Icon = icon || <CalIcon className={styles.icon} />;

  return (
    <div
      className={classNames(styles.container, {
        [styles.error]: invalid && !disabled,
        [styles.valid]: !disabled && (valid || dirtySinceLastSubmit),
        [styles.disabled]: disabled
      })}
    >
      <Label id={id} hidden={hideLabel}>
        {label}
        {!hideDateFormat && <span className={styles.textFormat}>{textFormat}</span>}
      </Label>
      <div className={classNames({ [styles.calendar]: !disabled }, className)}>
        <Flatpickr options={options} id={id} data-testid={id} />
        {showIcon && !disabled && Icon}
      </div>
      {submitError ? <p>{submitError}</p> : invalid && <Translation {...error.message} />}
    </div>
  );
};

const StarsDatePicker: React.FC<Props> = ({ validator, validationErrors, ...rest }) => {
  const locale = useSelector(select.locale.get);
  usePauseValidator();

  // TODO: make CalendarControls return type compatible with component prop
  return (
    <Field
      {...rest}
      locale={locale}
      name={rest.name || rest.id}
      component={CalendarControls as unknown as 'input'}
      validate={validator}
    />
  );
};

export default withValidator(StarsDatePicker);
